import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import type { Note, NoteAuthorKind, NoteType } from '@terra/api-client';
import { Badge, Button, Card, CardBody, EmptyState, Input, PageHeader, Spinner } from '@terra/ui';

import { api } from '../lib/api';

const NOTE_TYPES: NoteType[] = ['REFUND', 'WARRANTY', 'SHIPPING', 'RETURN', 'PAYMENT', 'GENERAL'];
const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();
const authorLabel = (k: NoteAuthorKind) => (k === 'IN_HOUSE' ? 'In-House' : 'Seller');

// ══════════════════════════════════════════════════════════════════
// ADD NOTE
// ══════════════════════════════════════════════════════════════════

export function AddNotePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [type, setType] = useState<NoteType>('GENERAL');
  const [description, setDescription] = useState('');

  const config = useQuery({
    queryKey: ['vendor-notes-config'],
    queryFn: () => api.notes.getVendorConfig(),
  });

  const save = useMutation({
    mutationFn: () => api.notes.createByVendor({ type, description }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vendor-notes'] });
      navigate('/notes');
    },
  });

  if (config.isLoading) {
    return (
      <div className="grid min-h-[160px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!config.data?.sellerCanAddNote) {
    return (
      <EmptyState
        title="Permission Denied"
        description="Sellers are not permitted to add notes at this time."
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Add New Note"
        description="Record a note for your store operations (refunds, warranty, shipping, etc.)."
      />
      <Card className="max-w-3xl">
        <CardBody>
          <h3 className="text-ink mb-5 text-base font-semibold">Note Information</h3>
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (description.trim().length > 0) save.mutate();
            }}
          >
            <div className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
              <label className="text-ink text-sm font-medium">Type</label>
              <select
                className="border-border bg-surface w-full rounded-md border px-3 py-2.5 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as NoteType)}
              >
                {NOTE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {titleCase(t)}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
              <div>
                <label className="text-ink text-sm font-medium">Description</label>
                <p className="text-ink-muted text-xs">(Max 900 character)</p>
              </div>
              <textarea
                className="border-border bg-surface h-44 w-full rounded-md border px-3 py-2.5 text-sm"
                maxLength={900}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="secondary"
                loading={save.isPending}
                disabled={!description.trim()}
              >
                Save
              </Button>
            </div>
            {save.isError && (
              <p className="text-right text-sm text-red-600">Could not save the note.</p>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// NOTE LIST
// ══════════════════════════════════════════════════════════════════

export function NoteListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [viewId, setViewId] = useState<string | null>(null);

  const config = useQuery({
    queryKey: ['vendor-notes-config'],
    queryFn: () => api.notes.getVendorConfig(),
  });
  const notes = useQuery({
    queryKey: ['vendor-notes', search],
    queryFn: () => api.notes.listForVendor(search ? { q: search } : {}),
  });

  const rows = notes.data ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Notes & Policies"
        actions={
          config.data?.sellerCanAddNote ? (
            <Button variant="secondary" onClick={() => navigate('/notes/new')}>
              Add Note
            </Button>
          ) : null
        }
      />

      <Card>
        <CardBody>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              placeholder="Type & Enter to Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-60"
            />
          </div>

          <div className="mt-5">
            {notes.isLoading ? (
              <div className="grid min-h-[160px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : rows.length === 0 ? (
              <EmptyState title="No notes found" description="There are no notes available." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-border text-ink-muted border-b text-xs font-semibold uppercase tracking-wide">
                      <th className="py-3 pr-4">#</th>
                      <th className="py-3 pr-4">Author</th>
                      <th className="py-3 pr-4">Type</th>
                      <th className="py-3 pr-4">Description</th>
                      <th className="py-3 pr-4 text-right">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((note: Note, idx: number) => (
                      <RowGroup
                        key={note.id}
                        note={note}
                        index={idx + 1}
                        expanded={viewId === note.id}
                        onView={() => setViewId(viewId === note.id ? null : note.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ── Table row + expandable detail ────────────────────────────────────────────

const ICON = {
  view: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
};

function RowIcon({ d }: { d: string }) {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}

function RowGroup({
  note,
  index,
  expanded,
  onView,
}: {
  note: Note;
  index: number;
  expanded: boolean;
  onView: () => void;
}) {
  return (
    <>
      <tr className="border-border border-b align-top">
        <td className="text-ink-muted py-4 pr-4">{index}</td>
        <td className="py-4 pr-4">
          <Badge tone={note.authorKind === 'IN_HOUSE' ? 'neutral' : 'gold'}>
            {authorLabel(note.authorKind)}
          </Badge>
        </td>
        <td className="text-ink py-4 pr-4 font-medium">{titleCase(note.type)}</td>
        <td className="text-ink-muted max-w-md py-4 pr-4">
          <span className="line-clamp-2">{note.description}</span>
        </td>
        <td className="py-4 pr-4">
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={onView}
              className="bg-gold-50 text-gold-700 hover:bg-gold-100 grid h-8 w-8 place-items-center rounded-full transition-colors"
              title="View"
            >
              <RowIcon d={ICON.view} />
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-border bg-surface-subtle/50 border-b">
          <td colSpan={5} className="px-4 py-4">
            <p className="text-ink-muted text-xs font-semibold uppercase tracking-wide">
              Full description
            </p>
            <p className="text-ink mt-1 whitespace-pre-wrap text-sm">{note.description}</p>
            <p className="text-ink-subtle mt-2 text-xs">
              Created {new Date(note.createdAt).toLocaleString()}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
